import React, { useEffect, useMemo, useState } from "react";
import { Tag, Plus, Pencil, Trash2, X, Check, ChevronDown } from "lucide-react";
import { api } from "../api";
import { useToast } from "../hooks/use-toast";
import { readableTextColor } from "../utils/contrast";

/**
 * SubtitleSelector — multi-select de "Subtítulo do catálogo" (badges que
 * aparecem no card do produto no Catálogo Online).
 *
 * Props:
 *   - value: array de subtitle IDs (string[])
 *   - onChange(string[]): callback ao adicionar/remover
 *   - className: classes extras
 *
 * Funcionalidades:
 *   - Selecionar múltiplos subtítulos
 *   - Criar novo subtítulo (texto + cor de fundo) inline
 *   - Editar texto + cor existente
 *   - Excluir (remove de todos os produtos automaticamente)
 */

// Paleta sugerida — usuário pode colar HEX livre também
const PRESET_COLORS = [
  "#FEF3C7", // âmbar
  "#DBEAFE", // azul
  "#DCFCE7", // verde
  "#FCE7F3", // rosa
  "#F3E8FF", // roxo
  "#FFE4E6", // pêssego
  "#E0F2FE", // celeste
  "#FED7AA", // laranja
  "#E5E5E5", // cinza neutro
];

export default function SubtitleSelector({ value = [], onChange, className = "" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newText, setNewText] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editColor, setEditColor] = useState(PRESET_COLORS[0]);
  const [search, setSearch] = useState("");
  const { toast } = useToast();

  const load = async () => {
    try {
      const r = await api.get("/catalog-subtitles");
      setItems(r.data || []);
    } catch {
      toast({ title: "Erro ao carregar subtítulos", variant: "destructive" });
    }
  };

  useEffect(() => { load(); }, []);

  const selectedItems = useMemo(
    () => (value || []).map(id => items.find(s => s.id === id)).filter(Boolean),
    [items, value]
  );

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(s => s.text.toLowerCase().includes(q));
  }, [items, search]);

  const toggle = (id) => {
    const next = (value || []).includes(id)
      ? value.filter(x => x !== id)
      : [...(value || []), id];
    onChange(next);
  };

  const handleCreate = async () => {
    const text = newText.trim();
    if (!text) return;
    try {
      const r = await api.post("/catalog-subtitles", { text, bg_color: newColor });
      await load();
      // Já marca o recém-criado
      if (!(value || []).includes(r.data.id)) onChange([...(value || []), r.data.id]);
      setNewText(""); setNewColor(PRESET_COLORS[0]); setCreating(false);
      toast({ title: "Subtítulo criado", description: text });
    } catch {
      toast({ title: "Erro ao criar subtítulo", variant: "destructive" });
    }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setEditText(s.text);
    setEditColor(s.bg_color || PRESET_COLORS[0]);
  };

  const saveEdit = async () => {
    const text = editText.trim();
    if (!text || !editId) return;
    try {
      await api.put(`/catalog-subtitles/${editId}`, { text, bg_color: editColor });
      await load();
      setEditId(null); setEditText("");
      toast({ title: "Subtítulo atualizado" });
    } catch {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Excluir o subtítulo "${s.text}"?\n\nEle será removido automaticamente de todos os produtos que o usam.`)) return;
    try {
      await api.delete(`/catalog-subtitles/${s.id}`);
      await load();
      if ((value || []).includes(s.id)) onChange(value.filter(x => x !== s.id));
      toast({ title: "Subtítulo excluído" });
    } catch {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  return (
    <div className={`relative ${className}`} data-testid="subtitle-selector">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="h-10 px-3 w-full text-sm inline-flex items-center justify-between gap-2 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 dark:bg-zinc-900 dark:border-zinc-700 dark:hover:bg-zinc-800 transition"
        data-testid="subtitle-selector-button"
      >
        <span className="inline-flex items-center gap-2 truncate">
          <Tag size={14} className="text-zinc-500 shrink-0" />
          <span className={`truncate ${selectedItems.length ? "text-zinc-900 dark:text-zinc-100" : "text-zinc-500"}`}>
            {selectedItems.length === 0
              ? "Subtítulo do catálogo (opcional)"
              : `${selectedItems.length} subtítulo${selectedItems.length !== 1 ? "s" : ""} selecionado${selectedItems.length !== 1 ? "s" : ""}`}
          </span>
        </span>
        <ChevronDown size={14} className="text-zinc-500 shrink-0" />
      </button>

      {/* Preview chips dos selecionados (sempre visível, ainda que dropdown fechado) */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="subtitle-selected-chips">
          {selectedItems.map(s => {
            const bg = s.bg_color || "#FEF3C7";
            return (
              <span
                key={s.id}
                className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                style={{ background: bg, color: readableTextColor(bg) }}
              >
                {s.text}
              </span>
            );
          })}
        </div>
      )}

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => { setOpen(false); setCreating(false); setEditId(null); }}
          />
          <div
            className="absolute z-50 mt-1 w-full min-w-[320px] rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xl max-h-96 overflow-hidden flex flex-col"
            data-testid="subtitle-selector-dropdown"
          >
            {/* Search */}
            <div className="p-2 border-b border-zinc-200 dark:border-zinc-700">
              <input
                type="text"
                placeholder="Buscar subtítulo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-8 px-2 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
                data-testid="subtitle-search"
              />
            </div>

            <div className="flex-1 overflow-auto">
              {filtered.map(s => {
                const checked = (value || []).includes(s.id);
                return (
                  <div key={s.id} className="group flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800">
                    {editId === s.id ? (
                      <div className="flex-1 space-y-1.5">
                        <input
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditId(null); }}
                          className="w-full h-7 px-2 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800"
                          autoFocus
                          data-testid="subtitle-edit-text"
                        />
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="text-[10px] text-zinc-500 mr-1">Cor:</span>
                          {PRESET_COLORS.map(c => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setEditColor(c)}
                              className={`w-5 h-5 rounded-full border-2 ${editColor === c ? "border-zinc-900 dark:border-white" : "border-transparent"}`}
                              style={{ background: c }}
                              title={c}
                            />
                          ))}
                          <input
                            type="color"
                            value={editColor}
                            onChange={(e) => setEditColor(e.target.value)}
                            className="w-5 h-5 rounded cursor-pointer ml-1"
                            title="Cor personalizada"
                          />
                          <div className="flex items-center gap-0.5 ml-auto">
                            <button type="button" onClick={saveEdit} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-950 rounded" data-testid="subtitle-edit-save"><Check size={12}/></button>
                            <button type="button" onClick={() => setEditId(null)} className="p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded"><X size={12}/></button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(s.id)}
                          className="h-4 w-4 cursor-pointer"
                          data-testid={`subtitle-checkbox-${s.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => toggle(s.id)}
                          className="flex-1 text-left"
                        >
                          <span
                            className="text-[11px] font-medium px-2 py-0.5 rounded-full inline-block"
                            style={{ background: s.bg_color || "#FEF3C7", color: readableTextColor(s.bg_color || "#FEF3C7") }}
                          >
                            {s.text}
                          </span>
                        </button>
                        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition">
                          <button type="button" onClick={() => startEdit(s)} className="p-1 text-zinc-500 hover:text-blue-600 rounded" title="Editar" data-testid={`subtitle-edit-${s.id}`}><Pencil size={11}/></button>
                          <button type="button" onClick={() => handleDelete(s)} className="p-1 text-zinc-500 hover:text-red-600 rounded" title="Excluir" data-testid={`subtitle-delete-${s.id}`}><Trash2 size={11}/></button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-3 py-3 text-xs text-zinc-500 italic">
                  {search ? "Nenhum subtítulo encontrado" : "Nenhum subtítulo cadastrado ainda"}
                </div>
              )}
            </div>

            {/* Create area */}
            <div className="border-t border-zinc-200 dark:border-zinc-700 p-2">
              {creating ? (
                <div className="space-y-1.5">
                  <input
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); if (e.key === "Escape") setCreating(false); }}
                    placeholder='Ex.: "Consulte disponibilidade"'
                    className="w-full h-8 px-2 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800"
                    autoFocus
                    data-testid="subtitle-create-text"
                  />
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[10px] text-zinc-500 mr-1">Cor:</span>
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setNewColor(c)}
                        className={`w-5 h-5 rounded-full border-2 ${newColor === c ? "border-zinc-900 dark:border-white" : "border-transparent"}`}
                        style={{ background: c }}
                        title={c}
                      />
                    ))}
                    <input
                      type="color"
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      className="w-5 h-5 rounded cursor-pointer ml-1"
                      title="Cor personalizada"
                    />
                    <div className="flex items-center gap-1 ml-auto">
                      <button type="button" onClick={handleCreate} disabled={!newText.trim()} className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 text-white rounded" data-testid="subtitle-create-confirm">Criar</button>
                      <button type="button" onClick={() => { setCreating(false); setNewText(""); }} className="p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded"><X size={12}/></button>
                    </div>
                  </div>
                  {/* Preview do badge novo */}
                  {newText.trim() && (
                    <div className="pt-1">
                      <span className="text-[10px] text-zinc-500 mr-1">Preview:</span>
                      <span
                        className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                        style={{ background: newColor, color: readableTextColor(newColor) }}
                      >
                        {newText.trim()}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="w-full inline-flex items-center gap-2 px-2 py-1.5 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 rounded"
                  data-testid="subtitle-create-button"
                >
                  <Plus size={14}/>
                  Criar novo subtítulo
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
